/**
 * Generic fallback theme: the original toon dressing (the art test look) for dungeons without a
 * bespoke theme (e.g. the training grounds).
 *  - one merged floor mesh (rooms + corridors) with the art test's dark, cool flagstone texture
 *  - KayKit wall pieces tiled along walkable boundaries (instanced), pillars at corners and as
 *    door posts where corridors meet rooms
 *  - wall torches (instanced flames + fake floor glow, no real lights) and banners
 *  - props scattered deterministically per room (layout.ts), instanced per kind
 * Everything static is instanced/merged: roughly 30 draw calls for the whole dungeon.
 * Dungeons with elevation use the themed builder (dungeon.ts) with GENERIC_THEME instead.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { DungeonDef, Polygon, Vec2 } from '@keystone/sim';
import type { ToonAssets } from '../assets';
import { type PropKind, type PropPlacement, computeWallLayout, placeWallDecor, scatterProps, tileRun } from '../layout';
import { type HullRegistry, addHull, flameMaterial, glowDiscMaterial, ownedToonMaterial, toonColor, toonMaterial } from '../style';
import type { DungeonTheme, PropKit } from './types';

const WALL_H = 0.7; // KayKit walls are 4 yd tall; 2.8 yd keeps the three-quarter camera clear
const WALL_TINT = new THREE.Color(0.8, 0.78, 0.9);
const PROP_TINT = new THREE.Color(0.92, 0.9, 0.96);
/** Floor albedo at 100% brightness (a touch under the art test: the dungeon has more floor on screen). */
export const FLOOR_BASE = 0.85;

export interface LegacyBuild {
  group: THREE.Group;
  floorMaterial: THREE.MeshToonMaterial;
  counts: { walls: number; props: number; torches: number; drawables: number };
  dispose(): void;
}

const EMPTY_KIT: PropKit = { clutter: [], cluster: [0, 0], spacing: 99, wall: [], wallSpacing: 99, foliage: [], foliageChance: 0, vines: 0, torchSpacing: 15, decals: 0 };

/**
 * The fallback theme. `legacy` keeps flat dungeons on the original builder; when a dungeon
 * without a bespoke theme has elevation, the themed builder uses these surfaces (canvas
 * flagstone, tinted stone, no ledges) so floors, stairs and walls still line up.
 */
export const GENERIC_THEME: DungeonTheme = {
  id: 'generic',
  name: 'Generic dungeon',
  legacy: true,
  floors: { flagstone: { texture: 'canvas:flagstone', tint: 0xffffff, tileYd: 8 } },
  surfaces: {
    ledgeTop: { tint: 0x6a6878, tileYd: 4 },
    stone: { texture: 'canvas:flagstone', tint: 0xb0aac0, tileYd: 4 },
    cliff: { texture: 'canvas:flagstone', tint: 0x8a8496, tileYd: 4 },
    trim: { tint: 0x8a8296, tileYd: 2 },
    wood: { tint: 0x6b4a2e, tileYd: 3 },
  },
  arch: { ledgeHeight: 0, roomMargin: 0, corridorMargin: 0, wallThickness: 1, wallHeights: { far: 2.8, near: 2.0, side: 2.8 }, baseY: -2.5, trim: false },
  room: () => ({ archetype: 'generic', floor: 'flagstone', light: 'warm' }),
  corridorFloor: () => 'flagstone',
  kits: { generic: EMPTY_KIT },
  heroes: { brazier: { half: [0.9, 0.9], h: 2, flame: true, light: 'warm' } },
  lighting: { background: 0x0a0b12, fog: [0.1, 0.11, 0.17], hemiSky: 0xcfe0ff, hemiGround: 0x3a3f4c, hemiIntensity: 0.5, key: 0xffffff, lift: [0.006, 0.009, 0.024] },
  glow: { warm: [1.0, 0.55, 0.2], jade: [0.22, 0.75, 0.58] },
};

/** The art test's flagstone canvas texture: mid-dark, low saturation, never emissive. */
export function makeFlagstoneTexture(maxAniso: number): THREE.CanvasTexture {
  const S = 1024;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d')!;
  let seed = 11;
  const rnd = (): number => (seed = (seed * 16807) % 2147483647) / 2147483647;
  // Cool grey-blue stone (art test direction); the warm/violet stones read purple under the grade lift.
  g.fillStyle = '#25282f';
  g.fillRect(0, 0, S, S);
  const pal = [[88, 92, 100], [96, 96, 97], [84, 90, 100], [92, 95, 98], [82, 86, 94], [92, 92, 94]];
  const stone = (x: number, y: number, w: number, h: number): void => {
    const p = pal[Math.floor(rnd() * pal.length)]!;
    const v = (rnd() - 0.5) * 16;
    const col = `rgb(${(p[0]! + v) | 0},${(p[1]! + v) | 0},${(p[2]! + v) | 0})`;
    const crack = rnd() < 0.18;
    const cx = rnd();
    rnd();
    for (const ox of [0, -S]) {
      const gx = x + ox + 5;
      const gy = y + 5;
      const gw = w - 10;
      const gh = h - 10;
      if (gx > S || gx + gw < 0) continue;
      g.fillStyle = col;
      g.beginPath();
      g.roundRect(gx, gy, gw, gh, 12);
      g.fill();
      g.lineWidth = 3;
      g.strokeStyle = 'rgba(255,255,255,0.07)';
      g.beginPath();
      g.moveTo(gx + 6, gy + gh - 6);
      g.lineTo(gx + 5, gy + 5);
      g.lineTo(gx + gw - 6, gy + 4);
      g.stroke();
      g.strokeStyle = 'rgba(0,0,0,0.22)';
      g.beginPath();
      g.moveTo(gx + gw - 3, gy + 8);
      g.lineTo(gx + gw - 3, gy + gh - 3);
      g.lineTo(gx + 8, gy + gh - 3);
      g.stroke();
      if (crack) {
        g.strokeStyle = 'rgba(20,18,26,0.45)';
        g.lineWidth = 2;
        g.beginPath();
        let px = gx + gw * cx;
        let py = gy + 8;
        g.moveTo(px, py);
        for (let k = 0; k < 5; k++) {
          px += Math.sin(k * 2.3 + cx * 9) * 0.5 * 22;
          py += gh / 6;
          g.lineTo(px, Math.min(py, gy + gh - 8));
        }
        g.stroke();
      }
    }
  };
  let y = 0;
  while (y < S) {
    let h = 104 + Math.floor(rnd() * 60);
    if (S - y - h < 100) h = S - y;
    const start = Math.floor(rnd() * S);
    let acc = 0;
    while (acc < S) {
      let w = 120 + Math.floor(rnd() * 130);
      if (S - acc - w < 110) w = S - acc;
      stone(start + acc, y, w, h);
      acc += w;
    }
    y += h;
  }
  for (let i = 0; i < 14000; i++) {
    g.fillStyle = rnd() < 0.5 ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.05)';
    const r = 1 + rnd() * 2.5;
    g.fillRect(rnd() * S, rnd() * S, r, r);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(1 / 8, 1 / 8); // UVs are in yards: one texture tile per 8 yd
  tex.anisotropy = Math.min(8, maxAniso);
  return tex;
}

/** Sim-space placement → matrix (y-up, sim y → -z). `yaw` is a three.js rotation.y. */
function placeMatrix(pos: Vec2, y: number, yaw: number, sx: number, sy: number, sz: number, extra?: THREE.Matrix4): THREE.Matrix4 {
  const m = new THREE.Matrix4().compose(new THREE.Vector3(pos.x, y, -pos.y), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw), new THREE.Vector3(sx, sy, sz));
  return extra ? m.multiply(extra) : m;
}

/** rotation.y that turns local +z toward the sim-space direction `n`. */
const yawFacing = (n: Vec2): number => Math.atan2(n.x, -n.y);

export function buildLegacyDungeon(d: DungeonDef, assets: ToonAssets, hulls: HullRegistry, maxAniso: number): LegacyBuild {
  const group = new THREE.Group();
  group.name = 'toon-dungeon';
  const owned: { dispose(): void }[] = [];
  let drawables = 0;
  const counts = { walls: 0, props: 0, torches: 0, drawables: 0 };

  // ---- backdrop (unlit void) --------------------------------------------------
  const b = d.bounds;
  const backGeo = new THREE.PlaneGeometry(b.w + 160, b.h + 160).rotateX(-Math.PI / 2);
  const backMat = new THREE.MeshBasicMaterial({ color: 0x07080d });
  owned.push(backGeo, backMat);
  const back = new THREE.Mesh(backGeo, backMat);
  back.position.set(b.x + b.w / 2, -0.08, -(b.y + b.h / 2));
  group.add(back);
  drawables++;

  // ---- floor ----------------------------------------------------------------------
  const walkable: Polygon[] = [...d.rooms.map((r) => r.shape), ...d.corridors.map((c) => c.shape)];
  const floorGeos = walkable.map((poly) => {
    const geo = new THREE.ShapeGeometry(new THREE.Shape(poly.points.map((p) => new THREE.Vector2(p.x, p.y))));
    // ShapeGeometry UVs are the shape coordinates (yards); lay flat (x, y) → (x, 0, -y).
    geo.rotateX(-Math.PI / 2);
    return geo;
  });
  const floorGeo = mergeGeometries(floorGeos, false) ?? new THREE.PlaneGeometry(1, 1);
  floorGeos.forEach((g) => g.dispose());
  const floorTex = makeFlagstoneTexture(maxAniso);
  const floorMaterial = ownedToonMaterial({ color: 0xffffff, map: floorTex });
  floorMaterial.userData['base'] = FLOOR_BASE;
  owned.push(floorGeo, floorTex, floorMaterial);
  const floor = new THREE.Mesh(floorGeo, floorMaterial);
  floor.receiveShadow = true;
  floor.name = 'floor';
  group.add(floor);
  drawables++;

  // ---- instancing helpers ---------------------------------------------------------
  /** Instance every mesh of `asset` at the given matrices (one InstancedMesh per sub-mesh). */
  const instance = (asset: string, mats: THREE.Matrix4[], opts: { tint?: THREE.Color; outline?: boolean; castShadow?: boolean; fallback?: () => THREE.BufferGeometry }): void => {
    if (mats.length === 0) return;
    let parts = assets.propMeshes(asset);
    let fallbackMat: THREE.Material | null = null;
    if (parts.length === 0) {
      if (!opts.fallback) return;
      const geo = opts.fallback();
      owned.push(geo);
      fallbackMat = toonColor(0x8a8296);
      parts = [{ geometry: geo, material: fallbackMat, matrix: new THREE.Matrix4() }];
    }
    for (const part of parts) {
      const mat = fallbackMat ?? toonMaterial(part.material as THREE.MeshStandardMaterial, { tint: opts.tint });
      const im = new THREE.InstancedMesh(part.geometry, mat, mats.length);
      mats.forEach((m, i) => im.setMatrixAt(i, m.clone().multiply(part.matrix)));
      im.instanceMatrix.needsUpdate = true;
      im.computeBoundingSphere();
      im.castShadow = opts.castShadow !== false;
      im.receiveShadow = true;
      im.userData['noStyle'] = true;
      group.add(im);
      drawables++;
      if (opts.outline !== false) {
        addHull(im, part.material as THREE.MeshStandardMaterial, hulls, 'prop');
        drawables++;
      }
    }
  };

  // ---- walls, corners, door posts ------------------------------------------------
  const layout = computeWallLayout(walkable);
  const roomCount = d.rooms.length;
  const wallMats: THREE.Matrix4[] = [];
  for (const run of layout.walls) {
    for (const piece of tileRun(run, 4, 0.5)) {
      wallMats.push(placeMatrix(piece.center, 0, yawFacing(piece.normal), piece.length / 4 + 0.02, WALL_H, 1));
    }
  }
  counts.walls = wallMats.length;
  instance('wall', wallMats, { tint: WALL_TINT, fallback: () => new THREE.BoxGeometry(4, 4, 1).translate(0, 2, 0) });

  const inside = (p: Vec2, except: number): boolean =>
    walkable.some((poly, i) => {
      if (i === except) return false;
      const pts = poly.points;
      let ins = false;
      for (let a = 0, c = pts.length - 1; a < pts.length; c = a++) {
        const A = pts[a]!;
        const C = pts[c]!;
        if (A.y > p.y !== C.y > p.y && p.x < ((C.x - A.x) * (p.y - A.y)) / (C.y - A.y) + A.x) ins = !ins;
      }
      return ins;
    });
  const pillarMats: THREE.Matrix4[] = [];
  for (const c of layout.corners) {
    // Skip corridor corners that sit inside a room (they'd stand in the walkable area).
    if (inside(c.pos, c.polyIndex)) continue;
    pillarMats.push(placeMatrix(c.pos, 0, 0, 0.62, 0.78, 0.62));
  }
  for (const o of layout.openings) {
    if (o.polyIndex >= roomCount) continue; // posts only on the room side of a doorway
    for (const p of [o.a, o.b]) pillarMats.push(placeMatrix({ x: p.x + o.normal.x * 0.45, y: p.y + o.normal.y * 0.45 }, 0, yawFacing(o.normal), 0.62, 0.82, 0.62));
  }
  instance('pillar_decorated', pillarMats, { tint: WALL_TINT, fallback: () => new THREE.BoxGeometry(1.6, 4, 1.6).translate(0, 2, 0) });

  // ---- torches, banners, braziers (flames + fake glow pools) ---------------------
  const decor = placeWallDecor(layout, { spacing: 15, maxTorches: 70, roomCount });
  const torchMats: THREE.Matrix4[] = [];
  const bannerMats: THREE.Matrix4[] = [];
  const flames: { pos: THREE.Vector3; w: number; h: number }[] = [];
  const glows: { pos: Vec2; r: number }[] = [];
  const tilt = new THREE.Matrix4().makeRotationX(0.35);
  for (const dc of decor) {
    const inward = { x: -dc.normal.x, y: -dc.normal.y };
    if (dc.kind === 'torch') {
      const m = placeMatrix({ x: dc.pos.x + inward.x * 0.12, y: dc.pos.y + inward.y * 0.12 }, 1.75, yawFacing(inward), 1, 1, 1, tilt);
      torchMats.push(m);
      flames.push({ pos: new THREE.Vector3(0, 0.75, 0).applyMatrix4(m), w: 0.55, h: 0.95 });
      glows.push({ pos: { x: dc.pos.x + inward.x * 1.6, y: dc.pos.y + inward.y * 1.6 }, r: 3.6 });
    } else {
      bannerMats.push(placeMatrix({ x: dc.pos.x + dc.normal.x * 0.5, y: dc.pos.y + dc.normal.y * 0.5 }, -0.15, yawFacing(inward), 0.75, 0.72, 0.75));
    }
  }
  counts.torches = torchMats.length;
  instance('torch_lit', torchMats, { outline: false, castShadow: false, fallback: () => new THREE.CylinderGeometry(0.08, 0.12, 0.9, 6) });
  instance('banner_patternA_red', bannerMats, { outline: false, castShadow: false });

  // ---- props ------------------------------------------------------------------------
  const corridorShapes = d.corridors.map((c) => c.shape);
  const all: PropPlacement[] = [];
  for (const room of d.rooms) {
    const avoid: Vec2[] = [d.entrance];
    for (const pk of d.packs) {
      if (pk.roomId !== room.id) continue;
      for (const sp of pk.spawns) avoid.push(sp.pos);
      for (const wp of pk.patrol ?? []) avoid.push(wp);
      // +patrols: keep clutter off waypoint paths (sampled every 3 yd) and wander circles.
      const pts = pk.path?.points.map((q) => q.pos) ?? [];
      if (pk.path?.mode === 'loop' && pts.length > 1) pts.push(pts[0]!);
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]!;
        const b = pts[i]!;
        const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 3));
        for (let k = 0; k <= n; k++) avoid.push({ x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n });
      }
      for (const sp of pk.spawns) {
        if (sp.movement?.type !== 'wander') continue;
        const r = sp.movement.radius;
        for (let k = 0; k < 8; k++) avoid.push({ x: sp.pos.x + Math.cos((k * Math.PI) / 4) * r, y: sp.pos.y + Math.sin((k * Math.PI) / 4) * r });
      }
    }
    all.push(...scatterProps(room, { blocked: corridorShapes, avoid, avoidRadius: 6 }));
  }
  counts.props = all.length;
  const byKind = new Map<PropKind, PropPlacement[]>();
  for (const p of all) byKind.set(p.kind, [...(byKind.get(p.kind) ?? []), p]);
  for (const [kind, list] of byKind) {
    if (kind === 'brazier') continue;
    instance(kind, list.map((p) => placeMatrix(p.pos, 0, p.rot, p.scale, p.scale, p.scale)), {
      tint: kind.startsWith('rubble') || kind === 'column' ? WALL_TINT : PROP_TINT,
      fallback: () => new THREE.BoxGeometry(1.4, 1.4, 1.4).translate(0, 0.7, 0),
    });
  }
  // Procedural braziers (art test design), merged per material.
  const braziers = byKind.get('brazier') ?? [];
  if (braziers.length) {
    const bowl = new THREE.CylinderGeometry(0.8, 0.38, 0.55, 10).translate(0, 1.6, 0);
    const rim = new THREE.TorusGeometry(0.8, 0.07, 6, 16).rotateX(Math.PI / 2).translate(0, 1.88, 0);
    const legs = [0, 1, 2].map((i) => {
      const a = (i / 3) * Math.PI * 2;
      const leg = new THREE.CylinderGeometry(0.07, 0.09, 1.5, 6);
      leg.rotateZ(Math.cos(a) * 0.3).rotateX(-Math.sin(a) * 0.3).translate(Math.cos(a) * 0.42, 0.72, Math.sin(a) * 0.42);
      return leg;
    });
    const bronzeGeo = mergeGeometries([bowl, rim])!;
    const legGeo = mergeGeometries(legs)!;
    const coalGeo = new THREE.SphereGeometry(0.6, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2).scale(1, 0.35, 1).translate(0, 1.8, 0);
    [bowl, rim, ...legs].forEach((g) => g.dispose());
    owned.push(bronzeGeo, legGeo, coalGeo);
    const ms = braziers.map((p) => placeMatrix(p.pos, 0, p.rot, 0.9, 0.9, 0.9));
    for (const [geo, mat, outline] of [
      [bronzeGeo, toonColor(0xd99a4e), true],
      [legGeo, toonColor(0x5a4a6a), true],
      [coalGeo, toonColor(0x441100, 0xb04810), false],
    ] as const) {
      const im = new THREE.InstancedMesh(geo, mat, ms.length);
      ms.forEach((m, i) => im.setMatrixAt(i, m));
      im.computeBoundingSphere();
      im.castShadow = true;
      group.add(im);
      drawables++;
      if (outline) {
        addHull(im, mat, hulls, 'prop');
        drawables++;
      }
    }
    for (const p of braziers) {
      flames.push({ pos: new THREE.Vector3(p.pos.x, 1.75 * 0.9, -p.pos.y), w: 1.1, h: 1.8 });
      glows.push({ pos: p.pos, r: 4.2 });
    }
  }

  // ---- flames (one instanced draw) and glow pools (one merged draw) ----------------
  if (flames.length) {
    const fGeo = new THREE.PlaneGeometry(1, 1).translate(0, 0.5, 0);
    const fMat = flameMaterial();
    owned.push(fGeo, fMat);
    const fm = new THREE.InstancedMesh(fGeo, fMat, flames.length);
    flames.forEach((f, i) => fm.setMatrixAt(i, new THREE.Matrix4().compose(f.pos, new THREE.Quaternion(), new THREE.Vector3(f.w, f.h, 1))));
    fm.computeBoundingSphere();
    fm.frustumCulled = false;
    group.add(fm);
    drawables++;
  }
  if (glows.length) {
    const discs = glows.map((g, i) => {
      const geo = new THREE.PlaneGeometry(g.r * 2, g.r * 2).rotateX(-Math.PI / 2).translate(g.pos.x, 0.025, -g.pos.y);
      geo.setAttribute('aSeed', new THREE.Float32BufferAttribute(new Array(geo.attributes['position']!.count).fill((i * 1.7) % 6), 1));
      return geo;
    });
    const gGeo = mergeGeometries(discs)!;
    discs.forEach((g) => g.dispose());
    const gMat = glowDiscMaterial([1.0, 0.55, 0.2], 0.42);
    owned.push(gGeo, gMat);
    const gm = new THREE.Mesh(gGeo, gMat);
    gm.renderOrder = 1;
    group.add(gm);
    drawables++;
  }

  // ---- entrance rune: faint stone-blue (a bright cyan ring read as a ground-target cursor) ----
  const entGeo = new THREE.RingGeometry(1.3, 1.6, 40).rotateX(-Math.PI / 2);
  const entMat = new THREE.MeshBasicMaterial({ color: 0x6f8296, transparent: true, opacity: 0.16, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
  owned.push(entGeo, entMat);
  const ent = new THREE.Mesh(entGeo, entMat);
  ent.position.set(d.entrance.x, 0.02, -d.entrance.y);
  group.add(ent);
  drawables++;

  counts.drawables = drawables;
  return {
    group,
    floorMaterial,
    counts,
    dispose() {
      group.traverse((o) => {
        if (o.userData['hull']) {
          hulls.prop.delete(o);
          hulls.unit.delete(o);
        }
        if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
      });
      group.removeFromParent();
      for (const o of owned) o.dispose();
    },
  };
}
